import { useState, useRef } from 'react';

interface Slide {
  id: string;
  type: 'floorplan' | 'exterior' | 'interior' | 'walkthrough' | 'video';
  title: string;
  imageUrl: string;
  visible: boolean;
  caption: string;
}

const THEMES = [
  {
    id: 'dark',
    label: 'Professional Dark',
    bg: '#1e293b',
    accent: '#14b8a6',
    preview: 'bg-slate-800',
  },
  {
    id: 'white',
    label: 'Clean White',
    bg: '#ffffff',
    accent: '#0891b2',
    preview: 'bg-white',
  },
  {
    id: 'blueprint',
    label: 'Blueprint',
    bg: '#0c4a6e',
    accent: '#38bdf8',
    preview: 'bg-sky-900',
  },
  {
    id: 'luxury',
    label: 'Luxury Gold',
    bg: '#1c1917',
    accent: '#d97706',
    preview: 'bg-stone-900',
  },
];

const ASSEMBLY_STEPS = [
  'Collecting floor plans...',
  'Adding renders...',
  'Inserting video clips...',
  'Applying theme...',
  'Finalizing deck...',
];

interface PresentationModeProps {
  darkMode: boolean;
}

export default function PresentationMode({ darkMode }: PresentationModeProps) {
  /* ------------------------------------------------------------------ */
  /* State handling */
  /* ------------------------------------------------------------------ */
  const [slides, setSlides] = useState<Slide[]>([
    {
      id: 'fp1',
      type: 'floorplan',
      title: '2D Floor Plan Overview',
      imageUrl:
        'https://public.readdy.ai/ai/img_res/ecf3fe6c2ed5b4646d2e0316fddc1bc4.jpg',
      visible: true,
      caption: 'Complete floor plan with room dimensions and layout',
    },
    {
      id: 'ext1',
      type: 'exterior',
      title: '3D Exterior Rendering',
      imageUrl:
        'https://readdy.ai/api/search-image?query=stunning%20photorealistic%203D%20architectural%20rendering%20modern%20house%20exterior%20beautiful%20landscaping%20dramatic%20sky%20professional%20visualization%20high%20quality&width=1200&height=675&seq=present-ext&orientation=landscape',
      visible: true,
      caption: 'Photorealistic exterior view with landscaping',
    },
    {
      id: 'int1',
      type: 'interior',
      title: 'Interior Living Space',
      imageUrl:
        'https://readdy.ai/api/search-image?query=beautiful%20photorealistic%20interior%20rendering%20modern%20house%20living%20room%20contemporary%20furniture%20natural%20light%20high%20quality%20architectural%20visualization&width=1200&height=675&seq=present-int&orientation=landscape',
      visible: true,
      caption: 'Fully furnished living room with modern design',
    },
    {
      id: 'walk1',
      type: 'walkthrough',
      title: '360° Virtual Walkthrough',
      imageUrl:
        'https://readdy.ai/api/search-image?query=3D%20walkthrough%20perspective%20view%20modern%20house%20interior%20hallway%20corridor%20architectural%20visualization%20immersive%20realistic%20render&width=1200&height=675&seq=present-walk&orientation=landscape',
      visible: true,
      caption: 'Immersive virtual tour experience',
    },
    {
      id: 'vid1',
      type: 'video',
      title: 'Animated Flythrough',
      imageUrl:
        'https://readdy.ai/api/search-image?query=cinematic%20architectural%20video%20still%20modern%20house%20aerial%20view%20dramatic%20lighting%20motion%20blur%20professional%20quality&width=1200&height=675&seq=present-vid&orientation=landscape',
      visible: true,
      caption: 'AI-generated video walkthrough',
    },
  ]);

  const [selectedTheme, setSelectedTheme] = useState('dark');
  const [activeSlideId, setActiveSlideId] = useState('fp1');
  const [showPreview, setShowPreview] = useState(false);
  const [previewSlideId, setPreviewSlideId] = useState('');
  const [isAssembling, setIsAssembling] = useState(false);
  const [assemblyStep, setAssemblyStep] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Branding
  const [clientName, setClientName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [footerText, setFooterText] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  /* ------------------------------------------------------------------ */
  /* Helper functions */
  /* ------------------------------------------------------------------ */
  const moveSlide = (id: string, direction: 'up' | 'down') => {
    const index = slides.findIndex((s) => s.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === slides.length - 1) return;

    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSlides[index], newSlides[targetIndex]] = [
      newSlides[targetIndex],
      newSlides[index],
    ];
    setSlides(newSlides);
  };

  const toggleSlideVisibility = (id: string) => {
    setSlides(
      slides.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s))
    );
  };

  const updateSlideCaption = (id: string, caption: string) => {
    setSlides(
      slides.map((s) => (s.id === id ? { ...s, caption } : s))
    );
  };

  const updateSlideTitle = (id: string, title: string) => {
    setSlides(
      slides.map((s) => (s.id === id ? { ...s, title } : s))
    );
  };

  const handleAutoAssemble = () => {
    setIsAssembling(true);
    setAssemblyStep(0);

    const interval = setInterval(() => {
      setAssemblyStep((prev) => {
        if (prev >= ASSEMBLY_STEPS.length - 1) {
          clearInterval(interval);
          setIsAssembling(false);
          showSuccessToast('Presentation deck assembled successfully!');
          return prev;
        }
        return prev + 1;
      });
    }, 800);
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleShare = async (method: string) => {
    if (method === 'link') {
      const url = `${window.location.origin}${window.location.pathname}?presentation=shared`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showSuccessToast('Presentation link copied to clipboard!');
      return;
    }

    if (method === 'email') {
      const subject = encodeURIComponent(`${projectName || 'Architecture'} Presentation${clientName ? ` for ${clientName}` : ''}`);
      const body = encodeURIComponent(
        `Hi,\n\nPlease find the architectural presentation${clientName ? ` for ${clientName}` : ''} at the link below:\n\n${window.location.href}\n\n${footerText || 'Best regards'}`
      );
      window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
      showSuccessToast('Email client opened!');
      return;
    }

    if (method === 'pdf') {
      window.print();
      showSuccessToast('Print / Save as PDF dialog opened!');
      return;
    }

    if (method === 'pptx') {
      showSuccessToast('PPTX export coming soon — stay tuned!');
      return;
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const openPreview = (id: string) => {
    setPreviewSlideId(id);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewSlideId('');
  };

  const getPreviewSlideIndex = () =>
    slides.findIndex((s) => s.id === previewSlideId);

  const navigatePreview = (direction: 'prev' | 'next') => {
    const currentIndex = getPreviewSlideIndex();
    if (currentIndex === -1) return;

    let newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0) newIndex = slides.length - 1;
    if (newIndex >= slides.length) newIndex = 0;

    setPreviewSlideId(slides[newIndex].id);
  };

  const visibleSlides = slides.filter((s) => s.visible);

  /* ------------------------------------------------------------------ */
  /* Render */
  /* ------------------------------------------------------------------ */
  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h3 className="text-base font-bold text-gray-900 mb-1">Presentation Mode</h3>
        <p className="text-xs text-gray-400">Auto-assemble floor plans, renders &amp; videos into a shareable client deck</p>
      </div>

      {/* Auto-Assemble Button */}
      <button
        onClick={handleAutoAssemble}
        disabled={isAssembling}
        className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 mb-5 transition-all cursor-pointer whitespace-nowrap ${
          isAssembling
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-violet-500 to-pink-500 text-white hover:shadow-lg hover:shadow-violet-500/30'
        }`}
      >
        {isAssembling ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>{' '}
            {ASSEMBLY_STEPS[assemblyStep]}
          </>
        ) : (
          <>
            <i className="ri-magic-line"></i> Auto-Assemble Deck
          </>
        )}
      </button>

      {/* Assembly Progress */}
      {isAssembling && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-5">
          <div className="space-y-2">
            {ASSEMBLY_STEPS.map((step, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 transition-all ${
                  index <= assemblyStep ? 'opacity-100' : 'opacity-30'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    index < assemblyStep
                      ? 'bg-violet-500'
                      : index === assemblyStep
                      ? 'bg-violet-500'
                      : 'bg-gray-200'
                  }`}
                >
                  {index < assemblyStep ? (
                    <i className="ri-check-line text-xs text-white"></i>
                  ) : index === assemblyStep ? (
                    <i className="ri-loader-4-line text-xs text-white animate-spin"></i>
                  ) : (
                    <span className="text-xs text-gray-400">
                      {index + 1}
                    </span>
                  )}
                </div>
                <p
                  className={`text-xs ${
                    index <= assemblyStep ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {step}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-gray-200 rounded-lg overflow-hidden h-1.5">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-pink-500 transition-all duration-700"
              style={{
                width: `${((assemblyStep + 1) / ASSEMBLY_STEPS.length) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Presentation Theme */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
        <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-3">
          Presentation Theme
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {THEMES.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setSelectedTheme(theme.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                selectedTheme === theme.id
                  ? 'bg-violet-50 border border-violet-300 text-violet-700'
                  : 'bg-gray-50 border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <div
                className={`w-5 h-5 rounded ${theme.preview} border border-gray-300 flex-shrink-0`}
              ></div>
              <span className="whitespace-nowrap">{theme.label}</span>
              {selectedTheme === theme.id && (
                <i className="ri-check-line text-violet-500 text-xs ml-auto"></i>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Branding Panel */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
        <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-3">
          Branding
        </h4>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">
              Client Name
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">
              Project Name
            </label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">
              Footer Text
            </label>
            <input
              type="text"
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              placeholder="© 2025 Your Architecture Firm"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-violet-400"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-6 h-6 object-contain rounded" />
            ) : (
              <i className="ri-image-line text-gray-400"></i>
            )}
            <span className="text-xs text-gray-500">{logoPreview ? 'Logo uploaded' : 'Architect Logo'}</span>
            <button
              onClick={() => logoInputRef.current?.click()}
              className="ml-auto text-xs text-violet-500 hover:text-violet-600 cursor-pointer whitespace-nowrap font-medium"
            >
              {logoPreview ? 'Change' : 'Upload'}
            </button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoUpload}
            />
          </div>
        </div>
      </div>

      {/* Slide Editor */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
        <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-3">
          Slide Editor ({visibleSlides.length} visible)
        </h4>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`bg-gray-50 border rounded-lg p-3 ${
                slide.visible
                  ? 'border-gray-200'
                  : 'border-gray-100 opacity-50'
              }`}
            >
              <div className="flex items-start gap-3 mb-2">
                {/* Move buttons */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveSlide(slide.id, 'up')}
                    disabled={index === 0}
                    className={`w-6 h-6 flex items-center justify-center rounded ${
                      index === 0
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 cursor-pointer'
                    }`}
                  >
                    <i className="ri-arrow-up-s-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => moveSlide(slide.id, 'down')}
                    disabled={index === slides.length - 1}
                    className={`w-6 h-6 flex items-center justify-center rounded ${
                      index === slides.length - 1
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 cursor-pointer'
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                </div>

                {/* Title & Caption */}
                <div className="flex-1">
                  <input
                    type="text"
                    value={slide.title}
                    onChange={(e) =>
                      updateSlideTitle(slide.id, e.target.value)
                    }
                    className="w-full px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-900 mb-1.5 focus:outline-none focus:border-violet-400"
                  />
                  <textarea
                    value={slide.caption}
                    onChange={(e) =>
                      updateSlideCaption(slide.id, e.target.value)
                    }
                    rows={2}
                    className="w-full px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600 resize-none focus:outline-none focus:border-violet-400"
                  ></textarea>
                </div>

                {/* Visibility toggle */}
                <button
                  onClick={() => toggleSlideVisibility(slide.id)}
                  className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer ${
                    slide.visible
                      ? 'bg-violet-50 text-violet-500 hover:bg-violet-100 border border-violet-200'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  <i
                    className={`${
                      slide.visible ? 'ri-eye-line' : 'ri-eye-off-line'
                    } text-sm`}
                  ></i>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 bg-white border border-gray-200 text-gray-500 rounded-full whitespace-nowrap">
                  Slide {index + 1}
                </span>
                <span className="text-xs text-gray-300">·</span>
                <span className="text-xs text-gray-400 capitalize">
                  {slide.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Preview Carousel */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
        <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-3">
          Slide Preview
        </h4>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {visibleSlides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => openPreview(slide.id)}
              className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                activeSlideId === slide.id
                  ? 'border-violet-400 shadow-md shadow-violet-200'
                  : 'border-gray-200 hover:border-violet-300'
              }`}
              style={{ width: '140px', height: '80px' }}
            >
              <div className="relative w-full h-full">
                <img
                  src={slide.imageUrl}
                  alt={slide.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-1 left-1.5 right-1.5">
                  <p className="text-white text-xs font-semibold truncate">
                    {index + 1}. {slide.title}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Share & Export */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-3">
          Share &amp; Export
        </h4>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleShare('link')}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-link text-sm"></i> Copy Link
          </button>
          <button
            onClick={() => handleShare('email')}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line text-sm"></i> Send Email
          </button>
          <button
            onClick={() => handleShare('pdf')}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-violet-500 to-pink-500 hover:shadow-lg hover:shadow-violet-500/30 text-white rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-pdf-line text-sm"></i> Download PDF
          </button>
          <button
            onClick={() => handleShare('pptx')}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:shadow-lg hover:shadow-orange-500/30 text-white rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-ppt-line text-sm"></i> Export PPTX
          </button>
        </div>
      </div>

      {/* Full-Screen Preview Modal */}
      {showPreview && previewSlideId && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <button
            onClick={closePreview}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white cursor-pointer z-10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <button
            onClick={() => navigatePreview('prev')}
            className="absolute left-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white cursor-pointer z-10"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={() => navigatePreview('next')}
            className="absolute right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white cursor-pointer z-10"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
          <div className="max-w-6xl w-full px-4">
            {slides.find((s) => s.id === previewSlideId) && (
              <div>
                <div className="rounded-xl overflow-hidden mb-4">
                  <img
                    src={slides.find((s) => s.id === previewSlideId)!.imageUrl}
                    alt={slides.find((s) => s.id === previewSlideId)!.title}
                    className="w-full h-auto"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {slides.find((s) => s.id === previewSlideId)!.title}
                  </h3>
                  <p className="text-white/60 text-sm">
                    {slides.find((s) => s.id === previewSlideId)!.caption}
                  </p>
                  <p className="text-white/30 text-xs mt-2">
                    Slide {getPreviewSlideIndex() + 1} of {slides.length}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 bg-gradient-to-r from-violet-500 to-pink-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}
    </div>
  );
}
